import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {NAV_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import getConfig from 'getConfig';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  OldSavedSearchesQuery,
  OldSavedSearchesQueryVariables,
} from 'search/saved-searches/__generated/OldSavedSearchesQuery';
import {markAsOpenedSavedSearch} from 'search/saved-searches/SavedSearchesDAO';
import {SavedSearchesDropdownMenu} from 'search/saved-searches/SavedSearchesDropdownMenu';
import {
  OLD_SAVED_SEARCHES_QUERY,
  refetchSavedSearchesForSection,
} from 'search/saved-searches/SavedSearchesQuery';
import SavedSearchModal from 'search/saved-searches/SavedSearchModal';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {SearchParams} from 'search/SearchConstants';
import EntityStore from 'stores/EntityStore';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import * as ModalContainer from 'ui-library/ModalContainer';
import sortedBy from 'util/sortedBy';

interface SavedSearchesDropdownProps {
  searchSection: SearchSection;
  onSearch: (searchParams: SearchParams) => void;
  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  activeSavedSearch?: SavedSearch;
  searchParams: SearchParams;
  isExpanded: boolean;
}

const SAVED_SEARCH_MODAL_KEY = 'SAVED_SEARCH_MODAL_KEY';

export default function SavedSearchesDropdown(props: SavedSearchesDropdownProps): JSX.Element {
  let savedSearchOptionDatas: Array<OptionData>;
  const savedSearchesResult = useBenchlingQuery<OldSavedSearchesQuery, OldSavedSearchesQueryVariables>(
    OLD_SAVED_SEARCHES_QUERY,
    {
      variables: {section: props.searchSection},
      renderCachedDataWhenRefetching: true,
    }
  );

  if (savedSearchesResult.type === QueryResultType.ERROR) {
    return <></>;
  } else if (savedSearchesResult.type === QueryResultType.PENDING) {
    savedSearchOptionDatas = [
      {
        disabled: true,
        name: <CenteredHorizontalThrobber suppressPadding={true} />,
        pureTextName: 'Loading...',
        value: null,
        className: css(styles.savedSearchOptionData),
      },
    ];
  } else {
    let sortedSavedSearches = savedSearchesResult.data.savedSearchesForSection;
    if (!getConfig('ENABLE_SIGHTGLASS_SHARED_SAVED_SEARCH')) {
      sortedSavedSearches = sortedBy(savedSearchesResult.data.savedSearchesForSection, ['name']);
    }
    savedSearchOptionDatas = sortedSavedSearches.map((savedSearch: SavedSearch) => ({
      disabled:
        EntityStore.currentUser() && savedSearch.ownerJson.id === EntityStore.currentUser().id
          ? false
          : !savedSearch.hasAccessToFiltersAndSection,
      name: savedSearch.name,
      pureTextName: savedSearch.name,
      value: savedSearch,
      className: css(styles.savedSearchOptionData),
    }));

    if (savedSearchOptionDatas.length === 0) {
      let name: string;
      if (getConfig('ENABLE_SIGHTGLASS_SHARED_SAVED_SEARCH')) {
        name = 'No recently used saved searches';
      } else {
        name = 'No saved searches';
      }
      savedSearchOptionDatas.push({
        disabled: true,
        name,
        pureTextName: name,
        value: null,
        className: css(styles.savedSearchOptionData),
      });
    }
  }

  const onSelect = (savedSearch: SavedSearch): void => {
    props.onSelectSavedSearch(savedSearch);
    if (savedSearch) {
      markAsOpenedSavedSearch(savedSearch.id);
    }
    refetchSavedSearchesForSection(props.searchSection);
  };

  const openSavedSearchModal = (): void => {
    ModalContainer.open(
      SAVED_SEARCH_MODAL_KEY,
      <SavedSearchModal
        searchSection={props.searchSection}
        onSelect={onSelect}
        closeModal={() => {
          ModalContainer.close(SAVED_SEARCH_MODAL_KEY);
        }}
        onSearch={props.onSearch}
        searchParams={props.searchParams}
      />
    );
  };

  // hiding feature behind config flag, because we still need search bar until we only return the
  // five most recent searches in this dropdown menu
  const menu = getConfig('ENABLE_SIGHTGLASS_SHARED_SAVED_SEARCH') ? (
    <SavedSearchesDropdownMenu
      optionDatas={savedSearchOptionDatas}
      onSelect={onSelect}
      selectedValue={props.activeSavedSearch}
      openSavedSearchModal={openSavedSearchModal}
    />
  ) : (
    <SearchableMenu
      scrollable={true}
      onSelect={(savedSearch: SavedSearch) => {
        props.onSelectSavedSearch(savedSearch);
        analytics.trackEvent(NAV_ANALYTICS_ACTIONS.OPENED_SAVED_SEARCH, {
          section: props.searchSection,
          api_id: props.activeSavedSearch,
        });
      }}
      optionDatas={[...savedSearchOptionDatas]}
      width={Menu.WIDTHS.MEDIUM}
      height={Menu.HEIGHTS.MEDIUM}
      selectedValue={props.activeSavedSearch}
      dropLeft={false}
    />
  );

  return (
    <Dropdown closeOnInsideClick={true} closeOnOutsideClick={true} menu={menu}>
      <span className={css(styles.toggle)}>
        <Button
          size={ButtonSize.SMALL}
          icon={props.isExpanded ? undefined : 'chevron-down'}
          text={props.isExpanded ? 'Saved Searches' : undefined}
          isDropdown={props.isExpanded ? true : undefined}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          dataTestAttrs={{component: 'SavedSearchesDropdown', element: 'button'}}
          aria-label="Saved Searches"
          aria-expanded={props.isExpanded}
        />
      </span>
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  buttonCaret: {
    color: Colors.GRAY_DARKER,
    marginLeft: 4,
    minWidth: 12,
  },
  savedSearchOptionData: {
    overflow: 'hidden',
  },
  toggle: {
    cursor: 'pointer',
    marginLeft: 6,
    whiteSpace: 'nowrap',
  },
});
